import type { FC } from 'react'
import { Delete } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid2,
  IconButton,
  Modal,
  styled,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  DialogActions,
} from '@mui/material'
import useSWR from 'swr'
import { useState } from 'react'
import {
  fetchTicketAttachments,
  getWorkflowFiveDetails,
  updateFileTicketRemovedByMa,
  uploadTicketAttachment,
} from '@/api/maintenance-tickets-api'
import PdfIcon from '@/assets/icons/pdf-icon.png'

interface ApproveModalProps {
  open: boolean
  onClose: () => void
  onApprove: (ticketId: number, note: string) => void
  data?: any
  isNested?: boolean
}

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
    },
  },
})

const ButtonGroup = styled(DialogActions)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 20,
  marginBottom: 20,
  marginLeft: 20,
  marginRight: 25,
  gap: 8,
  flexDirection: 'row',
  '& > :not(style) ~ :not(style)': {
    marginLeft: 0,
  },
  '@media (max-width: 600px)': {
    flexDirection: 'column-reverse',
    marginLeft: 18,
    marginRight: 18,
    '& > *': {
      width: '100%',
      marginBottom: 8,
    },
    '& > :not(style) ~ :not(style)': {
      marginLeft: 0,
    },
  },
})

export const ApproveDISModal: FC<ApproveModalProps> = ({
  open,
  onClose,
  onApprove,
  data,
  isNested,
}) => {
  const roleString = localStorage.getItem('role')
  const userRole = roleString ? JSON.parse(roleString) : ''
  const [remarks, setRemarks] = useState('')
  const [isApproving, setIsApproving] = useState(false)

  console.log('UNSIGNED DIS HERE')

  const getAllDISTicketAttachments = async () => {
    try {
      const response = await fetchTicketAttachments(data.id, 'DIS_QUOTATION')
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const fetchWorkflowFiveDetails = async () => {
    try {
      const response = await getWorkflowFiveDetails(data.id)
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const shouldFetchDISAttachments =
    (data?.currentLayerId === 4 &&
      data?.workFlowId === 5 &&
      userRole === 'Maintenance Admin') ||
    (data?.currentLayerId === 8 &&
      data?.workFlowId === 4 &&
      userRole === 'Maintenance Admin')

  const { data: disAttachments, mutate: mutateDISAttachments } = useSWR(
    shouldFetchDISAttachments ? `/v1/attachment` : null,
    getAllDISTicketAttachments,
    { revalidateOnFocus: false },
  )

  const { data: approvalDateAndGatePass } = useSWR(
    shouldFetchDISAttachments ? `/v1/ticket/details` : null,
    fetchWorkflowFiveDetails,
    { revalidateOnFocus: false },
  )

  const handleUploadDIS = async (
    file: File,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      await uploadTicketAttachment(data.id, 'DIS_QUOTATION', file)
      mutateDISAttachments()
    } catch (error) {
      console.error('Failed to upload dis attachment:', error)
    } finally {
      e.target.value = ''
    }
  }

  const handleRemoveFile = async (idToRemove: number) => {
    if (!disAttachments) return

    const remainingIds = disAttachments
      .filter((file: any) => file.id !== idToRemove)
      .map((file: any) => file.id)

    try {
      await updateFileTicketRemovedByMa(
        data.id,
        parseFloat(data.amount),
        remainingIds,
      )
      mutateDISAttachments()
    } catch (error) {
      console.error('Failed to remove file:', error)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={!isNested}
      maxWidth={!isNested ? 'sm' : false}
      PaperProps={{
        sx: isNested ? { width: 'auto', maxWidth: 'unset', minWidth: 650 } : {},
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: '2rem', pt: '1.5rem' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              sx={{
                fontFamily: 'Gabarito',
                fontWeight: 500,
                fontSize: '22px',
                color: '#505470',
              }}
            >
              Approve Ticket
            </Typography>
            <IconButton
              size="small"
              aria-label="close"
              sx={{
                color: '#505470',
                width: 20,
                height: 20,
              }}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 2, borderWidth: 2, color: '#B1B8BF' }} />
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          px: 5,
          pt: 3,
          pb: 0,
          minHeight: 230,
        }}
      >
        <FormControl fullWidth>
          <Typography component="span" sx={{ my: 2 }}>
            Attachment of Dealer Investment Summary{' '}
            <Typography component="span" color="red" ml={0.5}>
              *
            </Typography>
          </Typography>
          <Button
            variant="contained"
            component="label"
            sx={{ maxWidth: '40%' }}
          >
            <Box display="flex" alignItems="center">
              Attach
            </Box>
            <input
              type="file"
              hidden
              multiple
              accept="application/pdf"
              onChange={(e) => {
                const files = e.target.files
                if (files?.length) {
                  const firstFile = files[0]
                  handleUploadDIS(firstFile, e)
                }
              }}
            />
          </Button>
        </FormControl>

        {disAttachments?.length > 0 && (
          <>
            {disAttachments.map(
              (
                file:
                  | File
                  | {
                      fileType: string
                      fileName: string
                      url: string
                      presignedUrl: string
                    },
                index: number,
              ) => {
                const isFileInstance = file instanceof File
                const fileType = isFileInstance ? file.type : file.fileType
                const fileName = isFileInstance ? file.name : file.fileName
                const fileUrl = isFileInstance
                  ? URL.createObjectURL(file)
                  : file.presignedUrl

                return (
                  <Grid2
                    key={index}
                    container
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      marginTop: '1rem',
                    }}
                  >
                    {fileType?.startsWith('image/') ? (
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        width={'100%'}
                        height={'25px'}
                      >
                        <Grid2
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap={1}
                        >
                          <img
                            src={fileUrl}
                            alt="Preview"
                            width={25}
                            height={25}
                            style={{ objectFit: 'cover' }}
                          />
                          <Tooltip title={fileName} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              <a
                                target="_blank"
                                href={fileUrl}
                                style={{
                                  color: '#000',
                                  fontWeight: 500,
                                }}
                              >
                                {fileName}
                              </a>
                            </Typography>
                          </Tooltip>
                        </Grid2>
                      </Box>
                    ) : (
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        width={'100%'}
                        height={'25px'}
                      >
                        <Grid2
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap={1}
                        >
                          <img
                            src={PdfIcon}
                            alt="PDF"
                            width={25}
                            height={25}
                            style={{ objectFit: 'cover' }}
                          />
                          <Tooltip title={fileName} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              <a
                                target="_blank"
                                href={fileUrl}
                                style={{
                                  color: '#000',
                                  fontWeight: 500,
                                }}
                              >
                                {fileName}
                              </a>
                            </Typography>
                          </Tooltip>
                          <IconButton
                            onClick={() => handleRemoveFile((file as any).id)}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Grid2>
                      </Box>
                    )}
                  </Grid2>
                )
              },
            )}
          </>
        )}
        <Box sx={{ mt: 'auto', mb: 2 }}>
          <Typography sx={{ marginBottom: 1 }}>Remarks</Typography>
          <CustomTextField
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
            multiline
            maxRows={3}
            variant="outlined"
            placeholder="Enter remarks here..."
          />
        </Box>
      </DialogContent>
      <ButtonGroup>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#379F86',
            color: '#ffffff',
            width: { sx: '100%', md: 'auto' },
            opacity: !disAttachments?.length || isApproving ? 0.6 : 1,
          }}
          disabled={!disAttachments?.length || isApproving}
          onClick={async () => {
            setIsApproving(true)
            try {
              await onApprove(data.id, remarks ?? '')
            } finally {
              setIsApproving(false)
            }
          }}
        >
          {isApproving ? 'Approving…' : 'Approve'}
        </Button>
      </ButtonGroup>
    </Dialog>
  )
}
